import { useState } from "react";
import { Checkbox } from "@components";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import SortableTh from "./SortableTh";

const DraggableHeaderRow = ({
  visibleColumns,
  showPinnedLeft,
  onColumnReorder,
  config,
  isAllChecked,
  onSelectAllChanged,
  onSort,
  thClassName,
  thStyle,
  thContent,
  tableScrollRef,
}) => {
  const [activeDragId, setActiveDragId] = useState(null);
  const [activeDragWidth, setActiveDragWidth] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const activeDragCol = activeDragId
    ? visibleColumns.find((x) => x.key === activeDragId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      autoScroll={{ canScroll: (el) => el === tableScrollRef.current }}
      onDragStart={({ active }) => {
        setActiveDragId(active.id);
        setActiveDragWidth(active.rect.current.initial?.width ?? null);
      }}
      onDragEnd={({ active, over }) => {
        setActiveDragId(null);
        setActiveDragWidth(null);
        if (over && active.id !== over.id) {
          onColumnReorder(active.id, over.id);
        }
      }}
      onDragCancel={() => { setActiveDragId(null); setActiveDragWidth(null); }}
    >
      <tr>
        {config.selectable && (
          <th className="sticky top-0 left-0 bg-white z-20 border-b border-zinc-200 px-7 sm:w-12 sm:px-6">
            <Checkbox
              checked={isAllChecked}
              onChange={() => onSelectAllChanged()}
            />
          </th>
        )}
        {showPinnedLeft && (
          <th className="sticky top-0 left-0 bg-white z-20 border-b border-r border-zinc-200 px-4 w-12">
            {config.pinnedLeft.header ?? null}
          </th>
        )}
        <SortableContext
          items={visibleColumns.map((x) => x.id)}
          strategy={horizontalListSortingStrategy}
        >
          {visibleColumns.map((x) => (
            <SortableTh
              key={x.id}
              id={x.id}
              role={x.sortFunc ? "button" : ""}
              onClick={() => x.sortFunc && onSort(x.key)}
              className={thClassName(x)}
              style={thStyle(x)}
            >
              {thContent(x)}
            </SortableTh>
          ))}
        </SortableContext>
        {config.rowActions && (
          <th className="sticky top-0 right-0 z-10 bg-white border-b border-l border-zinc-200 px-7 sm:w-12 sm:px-6" />
        )}
      </tr>
      <DragOverlay dropAnimation={null} modifiers={[restrictToHorizontalAxis]}>
        {activeDragCol && (
          <div
            className={thClassName(activeDragCol)}
            style={{
              width: activeDragWidth ?? undefined,
              boxSizing: "border-box",
              position: "relative",
              background: "#e4e4e7",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {thContent(activeDragCol)}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableHeaderRow;
